/**
 * Module gating — the single source of truth (handoff §1, Variation A).
 *
 * Modules are disabled per organization. Platform and group admins are
 * `unrestricted` and see everything; org admins and viewers are `restricted`
 * and lose disabled modules entirely — no nav entry, no route, no cross-link.
 *
 * Like useCan, this is UX only. The server enforces every module endpoint and
 * returns 403 `module_disabled` regardless of what the UI shows (handoff §3);
 * the interceptor in shared/api handles that fallback. Hiding is not a
 * security control — the API check is.
 *
 * Never inline this logic elsewhere. If the semantics change (default closed,
 * group-admin overrides), they change here.
 */
import { useCallback } from "react";
import { useAuth } from "../AuthContext";
import type { AuthUser } from "../api/auth.schemas";

/** The seven known modules (handoff §4). */
export const KNOWN_MODULES = [
  "sysmon", "mitre", "vuln", "threat", "compliance", "fim", "cis",
] as const;
export type ModuleName = (typeof KNOWN_MODULES)[number];

export const MODULE_LABEL: Record<ModuleName, string> = {
  sysmon: "System Monitoring",
  mitre: "MITRE ATT&CK",
  vuln: "Vulnerability Assessment",
  threat: "Threat Hunting",
  compliance: "Security Compliance",
  fim: "File Integrity Monitoring",
  cis: "CIS Benchmark",
};

/**
 * Is `module` enabled for this user?
 *
 * Order matters and follows the handoff exactly:
 *   1. unrestricted users (platform/group admin) → always true.
 *   2. no modules dict → true (forward-compatible: unknown = enabled).
 *   3. otherwise only an EXPLICIT false hides it; a missing key stays visible,
 *      matching the server's "missing key = enabled" rule.
 */
export function isModuleEnabled(user: AuthUser | null, module: ModuleName): boolean {
  if (user === null) return false;
  if (user.module_enforcement === "unrestricted") return true;
  if (user.modules === null || user.modules === undefined) return true;
  return user.modules[module] !== false;
}

export function useModule() {
  const { user } = useAuth();
  const hasModule = useCallback(
    (module: ModuleName) => isModuleEnabled(user, module),
    [user],
  );
  return { hasModule };
}
